import { z } from 'zod';
import { isCalendarDateIso } from '../utils/calendar-date.js';
import { accountBillingModeSchema } from './account.js';
import { isoDateTimeSchema, uuidSchema } from './common.js';
import { tierAccessEnum } from './track.js';
import { TRIAL_DAYS_MAX, TRIAL_DAYS_MIN } from './trial-days.js';
export const subscriptionStatusEnum = z.enum([
    'trial',
    'active',
    'past_due',
    'paused',
    'archived',
    'cancelled',
]);
/**
 * Calendar date in `YYYY-MM-DD` form. Subscription periods live on a date
 * (not timestamp) grain — the BE schema uses Postgres `date`, so the wire
 * shape stays in lockstep.
 */
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD date');
/**
 * Tariff catalog row (admin-arch internal.tariffs / PRD §6.7).
 * Currency stored as kopecks (bigint) per ADR-010. Format to roubles on UI:
 * `formatRub(kop) = (kop/100).toFixed(2) + ' ₽'`.
 */
export const tariffSchema = z.object({
    code: tierAccessEnum,
    name: z.string().min(1).max(64),
    monthly_price_kop: z.number().int().nonnegative(),
    connection_fee_kop: z.number().int().nonnegative(),
    description: z.string().max(2000).nullable(),
    is_active: z.boolean(),
});
export const subscriptionSchema = z.object({
    id: uuidSchema,
    account_id: uuidSchema,
    tariff_id: tierAccessEnum,
    status: subscriptionStatusEnum,
    started_at: isoDateTimeSchema,
    current_period_end: isoDateTimeSchema,
    trial_ends_at: isoDateTimeSchema.nullable(),
    cancelled_at: isoDateTimeSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Lifecycle endpoints (TASK-071a + TASK-071b, Wave 2.14 BE-1)
//
// All endpoints under
//   POST /admin/accounts/:accountId/subscriptions/:id/{action}
//
// State machine (admin-prd §6.4, admin-arch §3.6):
//   trial    →  cancelled | archived | change-tariff, change-trial-end (in place)
//   active   →  paused | cancelled | change-tariff (in place) | extend
//   paused   →  cancelled | extend
//   cancelled → archived | trial (via grant-trial, a cancelled trial only)
//   archived → active (via activate, a paid one) | trial (via activate-trial,
//              an archived trial)
//
// `billing_mode` is the customer-account-level enum from `account.ts`
// (`manual` / `self_service`, mirrors admin-prd §6.5 Режим A / B).
//
// F-12 prorate on change-tariff of a paid subscription is MANDATORY
// (decisions-pre-architecture + TASK-071a description): `(new_price -
// old_price) * days_remaining / period_days` is always computed. A non-zero
// rounded delta materialises a pending `customers.payments` row (positive →
// charge placeholder, negative → credit placeholder). Wallet ledger deferred
// to v1.1. A trial is not paid for, so its tariff change has no prorate
// (PRD D-23).
// ---------------------------------------------------------------------------
/**
 * `POST /admin/accounts/:accountId/subscriptions/:id/change-tariff`
 *
 * `tariff_id` — `internal.tariffs.id` of the target tariff.
 * `billing_mode` — optional override of the account's billing posture; the
 * value lives on `customers.accounts.billing_mode` (the canonical enum is
 * `accountBillingModeSchema` from `account.ts`: `manual` / `self_service`).
 * `effective_date` — defaults to today (server clock). Used as the boundary
 * date when computing days_remaining for prorate; ignored for a trial.
 *
 * A paid subscription: F-12 prorate runs on every successful invocation. A
 * zero rounded delta produces no payment row; a non-zero delta produces a
 * pending `customers.payments` row carrying the charge / credit direction.
 *
 * A trial (PRD D-23, N-11): the tariff and its price snapshot change at once,
 * with no charge, refund or payment row (`prorate: null`) and the wallet
 * untouched; `period_end` — the trial's end — stays. Venue overrides are
 * cleared and `billing_mode` applies exactly as for a paid subscription.
 */
export const subscriptionChangeTariffSchema = z.object({
    tariff_id: uuidSchema,
    billing_mode: accountBillingModeSchema.optional(),
    effective_date: isoDateSchema.optional(),
    reason: z.string().max(500).optional(),
});
/**
 * `POST /.../pause` — from `active` only (PRD §6.4 — trial does not pause,
 * trial just expires). No prorate, no wallet adjustment.
 * Player auto-stops by polling (no DB-driven push).
 */
export const subscriptionPauseSchema = z.object({
    reason: z.string().max(500).optional(),
});
/**
 * `POST /.../cancel` — from `active | trial | paused`.
 *
 * `refund_remaining` — when true AND `days_remaining > 0`, materialise a
 * credit placeholder. Notes prefix encodes the billing posture so the
 * downstream wallet / payment-provider integration (v1.1) can route
 * correctly:
 *   - `manual` accounts  → `notes='refund_manual: ...'` — admin issues
 *     bank refund out of band.
 *   - `self_service` accounts → `notes='refund_self_service: ...'` —
 *     admin reviews and triggers ЮKassa / CloudPayments refund from the
 *     pending row.
 * Backend never auto-refunds through the payment gateway in MVP.
 */
export const subscriptionCancelSchema = z.object({
    reason: z.string().max(500).optional(),
    refund_remaining: z.boolean().default(false),
});
/**
 * `POST /.../archive` — from `cancelled` (PRD §6.4: archive is opt-in after
 * cancel for season clients, distinct from automatic delete chain) or from
 * `trial`, running or ended, in one action (PRD D-21): the music stops and the
 * account moves to «Архив». Archiving a trial sets `stopped_as_trial`;
 * archiving a cancelled subscription keeps the flag cancel wrote. A paid
 * subscription is cancelled first; any other status → 409
 * `SUBSCRIPTION_INVALID_STATE` ({@link subscriptionInvalidStateDetailsSchema}).
 * The body is the same for both: the typed «АРХИВ» of the paid dialog is the
 * admin's own safeguard.
 */
export const subscriptionArchiveSchema = z.object({
    reason: z.string().max(500).optional(),
});
/**
 * `POST /.../activate` — from `archived` only, and not an archived trial: one
 * with `stopped_as_trial` comes back as a trial through `activate-trial`
 * (PRD D-12 (c), D-21), and here gets 409 `SUBSCRIPTION_INVALID_STATE` with
 * `details.stopped_as_trial: true`. Restores to `active` with a fresh period
 * window. `tariff_id` optionally overrides the archived tariff.
 * `period_start` defaults to today; `period_end` defaults to period_start + 30d.
 */
export const subscriptionActivateSchema = z
    .object({
    tariff_id: uuidSchema.optional(),
    period_start: isoDateSchema.optional(),
    period_end: isoDateSchema.optional(),
})
    .refine((val) => val.period_start === undefined ||
    val.period_end === undefined ||
    val.period_start < val.period_end, {
    message: 'period_end must be strictly after period_start',
    path: ['period_end'],
});
/**
 * `POST /.../extend` — from `active | paused`. A trial, running or ended, is
 * refused with 409 `SUBSCRIPTION_INVALID_STATE` (PRD D-28): it becomes paid
 * through a recorded payment only. `new_period_end` must be strictly after the
 * current `period_end` (validated server-side because the existing value is
 * needed).
 */
export const subscriptionExtendSchema = z.object({
    new_period_end: isoDateSchema,
});
/**
 * `POST /.../change-trial-end` — «Изменить срок триала» (registration-approval
 * arch §8; PRD D-5, A-3, A-5). From `trial` only, running or ended — an ended
 * trial is still `trial`, and a later date reopens it; any other status → 409
 * `SUBSCRIPTION_INVALID_STATE`.
 *
 * `new_end_date` becomes `period_end`: the day «Триал истёк» appears; the
 * trial's last day is the day before (`lastDayOf`). The server checks it
 * against its today, the UTC date `GET /admin/server-date` returns:
 *   - from today to today + `TRIAL_DAYS_MAX` (365) days, earlier or later than
 *     the current end, else 400 `TRIAL_END_OUT_OF_RANGE` with
 *     `details: { earliest, latest }` (both `YYYY-MM-DD`). Today ends the
 *     trial at once; it keeps playing until a manager stops it (ADR-044);
 *   - not the current end, else 400 `TRIAL_END_UNCHANGED`.
 *
 * Free: neither the wallet nor any payment changes (A-5). Answers the
 * lifecycle envelope with `prorate: null`.
 */
export const subscriptionChangeTrialEndSchema = z
    .object({
    new_end_date: isoDateSchema.refine(isCalendarDateIso, 'Expected a calendar date'),
})
    .strict();
/** Refusals of `change-trial-end`, for the admin's error mapping. */
export const TRIAL_END_ERROR_CODES = {
    TRIAL_END_OUT_OF_RANGE: 'TRIAL_END_OUT_OF_RANGE',
    TRIAL_END_UNCHANGED: 'TRIAL_END_UNCHANGED',
};
/** A trial's length in whole days, 1–365 (PRD A-3). */
const trialDaysSchema = z.number().int().min(TRIAL_DAYS_MIN).max(TRIAL_DAYS_MAX);
/**
 * `POST /.../grant-trial` — «Назначить триал» (registration-approval arch §8;
 * PRD D-12 (b), A-10). From `cancelled` with `stopped_as_trial` only — a trial
 * a manager cancelled. Any other status, and a cancelled paid subscription,
 * → 409 `SUBSCRIPTION_INVALID_STATE` ({@link subscriptionInvalidStateDetailsSchema}).
 *
 * The subscription is a `trial` again on the tariff it had (A-10), from the
 * server's today — the UTC date `GET /admin/server-date` returns — to today +
 * `trial_days` (`period_end`, exclusive; the last day is `lastDayOf`). The
 * cancel stamp and `stopped_as_trial` are cleared, so the venue plays again
 * (ADR-044). Free: neither the wallet nor any payment changes. Answers the
 * lifecycle envelope with `prorate: null`; journalled as
 * `subscription.grant_trial` with the child `account.trial_granted`
 * `{ subscription_id, trial_days, period_end }`.
 */
export const subscriptionGrantTrialSchema = z
    .object({
    trial_days: trialDaysSchema,
})
    .strict();
/**
 * `POST /.../activate-trial` — «Активировать» an archived trial (arch §8; PRD
 * D-12 (c), D-21, A-11). From `archived` with `stopped_as_trial` only. Any
 * other status, and an archived paid subscription — `activate` takes that one
 * — → 409 `SUBSCRIPTION_INVALID_STATE`.
 *
 * The subscription is a `trial` on `tariff_id`, that tariff's price becoming
 * its snapshot (ADR-010), from the server's today to today + `trial_days`. The
 * archive, cancel and pause stamps and `stopped_as_trial` are cleared, and so
 * are its venues' preset overrides, as on every tariff change. An unknown
 * tariff → 400 `TARIFF_NOT_FOUND`. Free: neither the wallet nor any payment
 * changes. Answers the lifecycle envelope with `prorate: null`; journalled as
 * `subscription.activate_trial` with the child
 * `account.trial_activated_from_archive`
 * `{ subscription_id, tariff_id, tariff_name, trial_days, period_end }`.
 */
export const subscriptionActivateTrialSchema = z
    .object({
    tariff_id: uuidSchema,
    trial_days: trialDaysSchema,
})
    .strict();
/**
 * `error.details` of 409 `SUBSCRIPTION_INVALID_STATE` — the answer of every
 * lifecycle endpoint when the subscription's status does not allow the action,
 * read after the row lock (registration-approval arch §8). `allowed` — the
 * statuses the action is allowed from; `actual` — the status the row has;
 * `stopped_as_trial` — the row's flag (see `accountCardSubscriptionSchema`),
 * so a dialog that lost a race can tell an archived trial from an archived
 * paid subscription.
 */
export const subscriptionInvalidStateDetailsSchema = z.object({
    allowed: z.array(subscriptionStatusEnum),
    actual: z.string(),
    stopped_as_trial: z.boolean(),
});
/**
 * Common envelope returned by every lifecycle endpoint. Frontend can rely on
 * a stable shape regardless of which transition the admin invoked.
 */
export const subscriptionLifecycleResponseSchema = z.object({
    id: uuidSchema,
    status: subscriptionStatusEnum,
    tariff_id: uuidSchema,
    period_start: isoDateSchema,
    period_end: isoDateSchema,
    monthly_amount_kop: z.number().int().nonnegative(),
    paused_at: isoDateTimeSchema.nullable(),
    archived_at: isoDateTimeSchema.nullable(),
    cancelled_at: isoDateTimeSchema.nullable(),
    /**
     * Prorate side-effect — populated only when the transition materialised a
     * `customers.payments` row (change-tariff of a paid subscription with
     * non-zero rounded F-12 delta, or cancel with `refund_remaining: true` and
     * non-zero remaining days).
     */
    prorate: z
        .object({
        payment_id: uuidSchema,
        amount_kop: z.number().int(),
        direction: z.enum(['charge', 'credit']),
    })
        .nullable(),
});
// ---------------------------------------------------------------------------
// Wallet + subscription history (Wave 2.49 BE-1, TASK-071ab-followup).
//
// Wave 2.14 BE-1 deferred a dedicated wallet ledger to v1.1 and routed
// prorate / refund through `customers.payments` placeholder rows. Wave 2.49
// promotes wallet to a real account-level balance column
// (`customers.accounts.wallet_balance_kop`) and adds a read-only history
// endpoint so the FE account card can render past subscriptions instead of
// only the current row.
// ---------------------------------------------------------------------------
/**
 * Wallet balance in kopecks. Non-negative — the DB CHECK constraint and the
 * service `WALLET_INSUFFICIENT` 422 guard agree on this invariant.
 */
export const walletBalanceSchema = z.object({
    account_id: uuidSchema,
    balance_kop: z.number().int().nonnegative(),
});
/**
 * Single row in the subscription history list. `tariff_name` is denormalised
 * from `internal.tariffs` at read time so the FE can render without a second
 * round-trip; if the tariff was deleted post-hoc the field falls back to null.
 */
export const subscriptionHistoryItemSchema = z.object({
    id: uuidSchema,
    tariff_id: uuidSchema,
    tariff_name: z.string().nullable(),
    status: subscriptionStatusEnum,
    started_at: isoDateTimeSchema,
    ended_at: isoDateTimeSchema.nullable(),
    amount_kop_per_period: z.number().int().nonnegative(),
    created_by_user_id: uuidSchema.nullable(),
});
/**
 * Keyset-paginated history response. Cursor encodes `(created_at, id)` and is
 * opaque to the client — same conventions as the global audit list cursor.
 */
export const subscriptionHistoryResponseSchema = z.object({
    items: z.array(subscriptionHistoryItemSchema),
    next_cursor: z.string().nullable(),
});
/**
 * Query string for `GET /admin/accounts/:accountId/subscriptions/history`.
 * `limit` capped at 200 to match `paginationSchema`'s ceiling.
 */
export const subscriptionHistoryQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
});
//# sourceMappingURL=subscription.js.map